import { InteractionRequiredAuthError } from '@azure/msal-browser';
import type { IPublicClientApplication } from '@azure/msal-browser';
import { aliasesForCanonical } from '@pay-plan/shared';
import { graphRequest, graphSearchRequest, graphLocationRequest } from '../config/msal-config.js';
import { isPersonAccount } from '../utils/employee-filters.js';

/**
 * Translates browser-level TypeError ('Failed to fetch') into actionable copy.
 * Keeps the raw error in console.error for diagnosis. Does NOT retry — a
 * retry of the same fetch with the same (possibly empty) token won't help.
 */
function throwTranslatedFetchError(err: unknown, context: string): never {
  if (err instanceof TypeError) {
    // eslint-disable-next-line no-console
    console.error(`[graph] network/CORS failure in ${context}`, err);
    throw new Error(
      `Couldn't reach Microsoft Graph (${context}). This may be a VPN, browser extension (ad blocker), or temporary network issue. Try again in a moment.`,
    );
  }
  throw err;
}

interface GraphMeResponse {
  companyName?: string | null;
  department?: string | null;
}

export interface GraphUserProfile {
  companyName: string;
  department: string;
}

export interface GraphEmployee {
  id: string; // Azure AD OID — matches JWT oid claim; never null
  displayName: string;
  mail: string | null;
  userPrincipalName: string;
  // Raw Entra companyName; populated only by listUsersByLocation (not by
  // searchGraphUsers). Threaded through syncLocationEmployees so the
  // backend can detect alias matches (e.g. 'Value Center' → 'Ford') and
  // emit USER_LOCATION_ALIASED audit events. See shared LOCATION_ALIASES.
  companyName?: string;
}

type GraphUserRaw = GraphEmployee;

function mapGraphUser(u: GraphUserRaw): GraphEmployee {
  const base: GraphEmployee = {
    id: u.id,
    displayName: u.displayName,
    mail: u.mail,
    userPrincipalName: u.userPrincipalName,
  };
  if (typeof u.companyName === 'string' && u.companyName !== '') {
    base.companyName = u.companyName;
  }
  return base;
}

/**
 * Fetches the signed-in user's companyName + department from Microsoft
 * Graph. Called at provisioning time to hydrate the Pay-Plan-Manager
 * users row with rooftop/department info that isn't present in the
 * default ID token claims.
 */
export async function fetchGraphProfile(
  msalInstance: IPublicClientApplication,
): Promise<GraphUserProfile> {
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) {
    throw new Error('No signed-in account available for Graph lookup');
  }

  const tokenResponse = await msalInstance.acquireTokenSilent({
    ...graphRequest,
    account: accounts[0],
  });

  let res: Response;
  try {
    res = await fetch('https://graph.microsoft.com/v1.0/me?$select=companyName,department', {
      headers: { Authorization: `Bearer ${tokenResponse.accessToken}` },
    });
  } catch (err) {
    throwTranslatedFetchError(err, 'profile lookup');
  }

  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`Graph /me failed: ${res.status} ${res.statusText} — ${body}`);
  }

  const data = (await res.json()) as GraphMeResponse;
  return {
    companyName: (data.companyName ?? '').trim(),
    department: (data.department ?? '').trim(),
  };
}

interface GraphUsersSearchResponse {
  value: GraphUserRaw[];
}

/**
 * Searches all tenant users by displayName using Graph $search.
 * Requires User.ReadBasic.All admin consent (granted 2026-04-15).
 *
 * Uses URLSearchParams for outer encoding — do NOT encodeURIComponent the
 * inner query value, as Graph treats %20 literally inside $search and
 * would return zero results for multi-word names.
 */
export async function searchGraphUsers(
  msalInstance: IPublicClientApplication,
  query: string,
): Promise<GraphEmployee[]> {
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (account === undefined) return [];

  let tokenResponse;
  try {
    tokenResponse = await msalInstance.acquireTokenSilent({
      ...graphSearchRequest,
      account,
    });
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      // Newly-granted admin consent may not yet be reflected in the cached token
      tokenResponse = await msalInstance.acquireTokenPopup({
        ...graphSearchRequest,
        account,
      });
    } else {
      throw err;
    }
  }

  const sanitized = query.replace(/"/g, '');
  const params = new URLSearchParams({
    $search: `"displayName:${sanitized}"`,
    // companyName included so the picker can display the raw Entra rooftop
    // tag next to each result. Doubles as an in-app diagnostic when an
    // expected employee is missing from a location-scoped roster — surfaces
    // the exact Entra value to feed back into LOCATION_ALIASES.
    $select: 'id,displayName,mail,userPrincipalName,companyName',
    $top: '25',
    $count: 'true', // required alongside ConsistencyLevel: eventual for $search
  });

  let res: Response;
  try {
    res = await fetch(`https://graph.microsoft.com/v1.0/users?${params.toString()}`, {
      headers: {
        Authorization: `Bearer ${tokenResponse.accessToken}`,
        ConsistencyLevel: 'eventual', // mandatory for $search; omitting returns 400
      },
    });
  } catch (err) {
    throwTranslatedFetchError(err, 'employee search');
  }

  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`Graph /users search failed: ${res.status} ${res.statusText} — ${body}`);
  }

  const data = (await res.json()) as GraphUsersSearchResponse;
  return data.value.map(mapGraphUser).filter(isPersonAccount);
}

/**
 * Returns all users at a given rooftop by filtering on companyName.
 * Requires User.Read.All admin consent (granted by IT on 2026-04-15).
 *
 * $filter on companyName requires Graph's advanced-query mode, which is
 * enabled by ConsistencyLevel: eventual + $count=true. Advanced-query
 * mode does NOT support $expand, so manager IDs aren't fetched here —
 * only the fields the picker needs for display. Manager relationships
 * live on the backend users table and are synced separately.
 *
 * Single quotes inside companyName are escaped per OData spec ('').
 */
export async function listUsersByLocation(
  msalInstance: IPublicClientApplication,
  companyName: string,
): Promise<GraphEmployee[]> {
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (account === undefined) return [];

  let tokenResponse;
  try {
    tokenResponse = await msalInstance.acquireTokenSilent({
      ...graphLocationRequest,
      account,
    });
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      tokenResponse = await msalInstance.acquireTokenPopup({
        ...graphLocationRequest,
        account,
      });
    } else {
      throw err;
    }
  }

  // Expand the canonical rooftop into its alias set (e.g. Ford → [Ford,
  // Value Center, VC]) so a single Graph call covers all Entra values that
  // map to this rooftop. Single quotes inside values are escaped per OData.
  const aliases = aliasesForCanonical(companyName);
  const filter = aliases.map((name) => `companyName eq '${name.replace(/'/g, "''")}'`).join(' or ');
  const params = new URLSearchParams({
    $filter: filter,
    // companyName is required so the backend can distinguish a canonical
    // match ('Ford') from an alias match ('Value Center' → Ford) and emit
    // the USER_LOCATION_ALIASED audit event accordingly.
    $select: 'id,displayName,mail,userPrincipalName,companyName',
    $count: 'true',
    $top: '100',
  });

  // Follow @odata.nextLink pagination until exhausted. Graph caps a single
  // page at $top (100 here); combined-alias rosters can exceed that at
  // larger tenants, silently dropping employees past the first page if we
  // only read one response. Each follow-up request replays the same
  // Authorization + ConsistencyLevel headers on the server-provided URL.
  let nextUrl: string | null = `https://graph.microsoft.com/v1.0/users?${params.toString()}`;
  const collected: GraphUserRaw[] = [];
  while (nextUrl !== null) {
    let res: Response;
    try {
      res = await fetch(nextUrl, {
        headers: {
          Authorization: `Bearer ${tokenResponse.accessToken}`,
          ConsistencyLevel: 'eventual',
        },
      });
    } catch (err) {
      throwTranslatedFetchError(err, 'location roster');
    }

    if (!res.ok) {
      const body = await res.text().catch(() => '');
      throw new Error(
        `Graph /users location filter failed: ${res.status} ${res.statusText} — ${body}`,
      );
    }

    const data = (await res.json()) as GraphUsersSearchResponse & {
      '@odata.nextLink'?: string;
    };
    collected.push(...data.value);
    nextUrl = data['@odata.nextLink'] ?? null;
  }

  return collected.map(mapGraphUser).filter(isPersonAccount);
}
